import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { motion } from "framer-motion";
import { LoginForm } from "./LoginForm";
import CodeLoginForm from "./CodeLoginForm";
import RegisterForm from "./RegisterForm";
import PasswordResetDialog from "./PasswordResetDialog";
import { TwoFactorVerificationDialog } from "./TwoFactorVerificationDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { ucApi } from "@/services/api/uc";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface LoginRightPanelProps {
  loaded: boolean;
  initialEmail?: string;
  redirectUrl?: string;
}

const LoginRightPanel = ({ loaded, initialEmail = "", redirectUrl = "" }: LoginRightPanelProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const targetUrl = searchParams.get('redirect') || '/profile';
  const [isLogin, setIsLogin] = useState(true);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [twoFactorOpen, setTwoFactorOpen] = useState(false);
  const [loginSuccessData, setLoginSuccessData] = useState<any>(null);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [loginMethod, setLoginMethod] = useState<"password" | "code">("password");
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [showAgreementError, setShowAgreementError] = useState(false);
  const agreementRef = useRef<HTMLDivElement>(null);
  const { setUser } = useAuth();

  useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
    }
  }, [initialEmail]);

  useEffect(() => {
    // Reset the error state after animation completes
    if (showAgreementError) {
      const timer = setTimeout(() => {
        setShowAgreementError(false);
      }, 820); // Duration slightly longer than the animation
      return () => clearTimeout(timer);
    }
  }, [showAgreementError]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  // Function to handle successful login
  const handleLoginSuccess = (userData: any, userEmail: string, password: string = "", authType?: string) => {
    if (!agreementAccepted) {
      setShowAgreementError(true);
      if (agreementRef.current) {
        agreementRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return { success: false, error: "You must agree to our terms and conditions" };
    }
    
    if (authType && authType === 'OTP') {
      // 需要二步验证
      setLoginSuccessData(userData);
      setEmail(userEmail);
      setPassword(password);
      setTwoFactorOpen(true);
    } else {
      // 不需要二步验证，直接登录成功
      navigate(redirectUrl, { replace: true });
    }
  };

  // Function for code login success
  const handleCodeLoginSuccess = (userData: any, userEmail: string) => {
    if (!agreementAccepted) {
      setShowAgreementError(true);
      if (agreementRef.current) {
        agreementRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return { success: false, error: "You must agree to our terms and conditions" };
    }
    
    setEmail(userEmail);
    handleLoginSuccess(userData, userEmail);
  };

  // Function to verify 2FA code
  const handleVerify2FA = async (code: string) => {
    try {
      const response = await ucApi.auth.codeVerify({
        email: email,
        code: code,
        type: 'login'
      });

      if (response.code === 1000) {
        // 验证成功后，保存登录信息
        if (loginSuccessData && loginSuccessData.token) {
          document.cookie = `anzir_token=Anzir ${loginSuccessData.token}; path=/; max-age=${loginSuccessData.expiresIn}`;
        }
        if (loginSuccessData && loginSuccessData.refreshToken) {
          localStorage.setItem("anzir_refresh_token", loginSuccessData.refreshToken);
        }
        if (loginSuccessData && loginSuccessData.user) {
          localStorage.setItem("anzir_user", JSON.stringify(loginSuccessData.user));
          // 设置AuthContext中的用户
          setUser(loginSuccessData.user);
        }
        return { success: true };
      } else {
        return {
          success: false,
          error: response.message || "Invalid verification code"
        };
      }
    } catch (error) {
      console.error("Verification error:", error);
      return {
        success: false,
        error: "An error occurred during verification"
      };
    }
  };


  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: loaded ? 1 : 0, x: loaded ? 0 : 50 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      className="flex flex-col w-full md:w-1/2 items-center justify-center p-6 md:p-10 lg:p-16 relative z-10"
    >
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ChevronLeft size={16} className="mr-1" />
          Back to Home
        </Link>

        <div className="bg-black/60 p-8 rounded-xl shadow-2xl border border-white/10 backdrop-blur-sm">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                {isLogin ? "Welcome Back" : "Join Anzir"}
              </h1>
              <p className="text-gray-400">
                {isLogin
                  ? "Sign in to access your account and continue your MysteryAct journey"
                  : "Create an account and start exploring the world of MysteryAct games"}
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            {isLogin ? (
              <Tabs
                defaultValue="password"
                value={loginMethod}
                onValueChange={(value) => setLoginMethod(value as "password" | "code")}
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-2 mb-6 bg-black/40">
                  <TabsTrigger value="password" className="data-[state=active]:bg-anzir/20">
                    Password
                  </TabsTrigger>
                  <TabsTrigger value="code" className="data-[state=active]:bg-anzir/20">
                    Verification Code
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="password" className="space-y-4">
                  <LoginForm
                    onLoginSuccess={handleLoginSuccess}
                    onForgotPassword={() => setResetPasswordOpen(true)}
                    initialEmail={email}
                    redirectUrl={redirectUrl}
                    agreementAccepted={agreementAccepted}
                    onAgreementError={() => setShowAgreementError(true)}
                  />
                </TabsContent>
                <TabsContent value="code">
                  <CodeLoginForm
                    onLoginSuccess={handleCodeLoginSuccess}
                    initialEmail={email}
                    redirectUrl={redirectUrl}
                    agreementAccepted={agreementAccepted}
                    onAgreementError={() => setShowAgreementError(true)}
                  />
                </TabsContent>
              </Tabs>
            ) : (
              <RegisterForm 
                agreementAccepted={agreementAccepted} 
                onAgreementError={() => setShowAgreementError(true)}
              />
            )}
            
            <motion.div 
              ref={agreementRef}
              animate={showAgreementError ? { 
                x: [0, -10, 10, -10, 10, 0],
                transition: { duration: 0.5 }
              } : {}}
              className={cn(
                "mt-4 flex items-center space-x-3 p-2 rounded-md",
                showAgreementError ? "bg-[#ff6c24]/20 border border-[#ff6c24]/50" : ""
              )}
            >
              <div 
                className={cn(
                  "h-4 w-4 rounded-full flex items-center justify-center border cursor-pointer",
                  agreementAccepted 
                    ? "bg-[#ff6c24] border-[#ff6c24]" 
                    : showAgreementError 
                      ? "border-[#ff6c24]" 
                      : "border-gray-500"
                )}
                onClick={() => setAgreementAccepted(!agreementAccepted)}
              >
                {agreementAccepted && (
                  <div className="h-1.5 w-1.5 rounded-full bg-white"></div>
                )}
              </div>
              <label htmlFor="agreement" className="text-sm text-gray-400 cursor-pointer" onClick={() => setAgreementAccepted(!agreementAccepted)}>
                I agree to the <Link to="/agreement/player" className="text-anzir hover:underline">{t('footer.userPolicy', 'User Policy')}</Link>{" "}
                and{" "}
                <Link to="/agreement/privacy-policy" className="text-anzir hover:underline">{t('footer.privacy', 'Privacy')}</Link>
              </label>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 1 }}
            className="mt-6 text-center text-gray-400"
          >
            <p>
              {isLogin ? "Don't have an account?" : "Already have an account?"}
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setEmail("");
                }}
                className="text-anzir hover:underline ml-2"
              >
                {isLogin ? "Sign Up" : "Sign In"}
              </button>
            </p>
          </motion.div>
        </div>
      </div>

      {/* Password Reset Dialog */}
      <PasswordResetDialog
        open={resetPasswordOpen}
        onOpenChange={setResetPasswordOpen}
        email={email}
        onEmailChange={handleEmailChange}
      />

      {/* Two-Factor Verification Dialog */}
      <TwoFactorVerificationDialog
        open={twoFactorOpen}
        onOpenChange={setTwoFactorOpen}
        onVerify={handleVerify2FA}
        email={email}
        password={password}
        redirectUrl={redirectUrl}
      />
    </motion.div>
  );
};

export default LoginRightPanel;
